import { type CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"

export type ChartImageModalProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  chartConfig: CustomChart
  defaultProject?: {
    project_id: string
    name: string
    logo: string
  } | null
  defaultShowSources?: boolean
  defaultShowFootnote?: boolean
  renderContent: (props: { projectLogo?: React.ReactNode }) => React.ReactNode
}

export type ChartImageState = {
  presetId?: string
  theme: "default" // can be extended to allow for custom or project-based themes in the future
  showBackground: boolean
  background: {
    type: "preset" | "custom"
    presetKey?: string
    customColor?: string
  }
  aspectRatio: "1/1" | "16/9"
  sourcesAndFootnoteDisplay: null | "inside-card" | "out-of-card"
  showSources?: boolean
  showFootnote?: boolean
  padding: "small" | "medium" | "large"

  image: {
    type: "none" | "project" | "custom"
    projectId?: string
    projectName?: string
    projectLogo?: string
    customUrl?: string
  }
}

export const paddingPixelsForPadding = {
  small: 10,
  medium: 30,
  large: 60,
}

export const cardPaddingPixelsForPadding = {
  small: 20,
  medium: 25,
  large: 30,
}

export const nonCardPaddingPixelsForPadding = {
  small: 10,
  medium: 20,
  large: 30,
}

export const dimensionsForAspectRatio: Record<
  ChartImageState["aspectRatio"],
  {
    width: number
    height: number
  }
> = {
  "1/1": {
    width: 500,
    height: 500,
  },
  "16/9": {
    width: 900,
    height: 500,
  },
}

export const maxWidth = Math.max(
  ...Object.values(dimensionsForAspectRatio).map((d) => d.width)
)
export const maxHeight = Math.max(
  ...Object.values(dimensionsForAspectRatio).map((d) => d.height)
)

// export const canvasWidth = maxWidth + paddingPixelsForPadding.large * 2
// export const canvasHeight = maxHeight + paddingPixelsForPadding.large * 2

type MakePreset = (props: {
  defaultProject: ChartImageModalProps["defaultProject"]
  defaultShowSources: ChartImageModalProps["defaultShowSources"]
  defaultShowFootnote: ChartImageModalProps["defaultShowFootnote"]
}) => ChartImageState

export const presets: Record<
  string,
  {
    id: string
    label: string
    makePreset: MakePreset
  }
> = {
  default: {
    id: "default",
    label: "Default",
    makePreset: ({
      defaultProject,
      defaultShowSources,
      defaultShowFootnote,
    }) => ({
      presetId: "default",
      theme: "default",
      showBackground: true,
      background: { type: "preset", presetKey: "default" },
      aspectRatio: "16/9",
      sourcesAndFootnoteDisplay: "out-of-card",
      showSources: defaultShowSources ?? true,
      showFootnote: defaultShowFootnote ?? true,
      padding: "medium",
      image: {
        type: "none",
        projectId: defaultProject?.project_id,
        projectName: defaultProject?.name,
        projectLogo: defaultProject?.logo,
      },
    }),
  },
  "social-media": {
    id: "social-media",
    label: "Social media",
    makePreset: ({
      defaultProject,
      defaultShowSources,
      defaultShowFootnote,
    }) => ({
      presetId: "social-media",
      theme: "default",
      showBackground: false,
      background: { type: "preset", presetKey: "default" },
      aspectRatio: "1/1",
      sourcesAndFootnoteDisplay: "inside-card",
      showSources: true,
      showFootnote: true,
      padding: "small",
      image: {
        type: "none",
        projectId: defaultProject?.project_id,
        projectName: defaultProject?.name,
        projectLogo: defaultProject?.logo,
      },
    }),
  },
  "slide-deck": {
    id: "slide-deck",
    label: "Slide deck",
    makePreset: ({
      defaultProject,
      defaultShowSources,
      defaultShowFootnote,
    }) => ({
      presetId: "slide-deck",
      theme: "default",
      showBackground: true,
      background: { type: "preset", presetKey: "chart2" },
      aspectRatio: "16/9",
      sourcesAndFootnoteDisplay: "out-of-card",
      showSources: defaultShowSources ?? true,
      showFootnote: defaultShowFootnote ?? true,
      padding: "large",
      image: {
        type: defaultProject ? "project" : "none",
        projectId: defaultProject?.project_id,
        projectName: defaultProject?.name,
        projectLogo: defaultProject?.logo,
      },
    }),
  },
}

export const transitionArgs = "300ms cubic-bezier(.27,.01,0,1)"
